import { useState } from 'react';

import {
  ActionPanel as ActionPanelUI,
  DropdownMenu,
  InteractiveTable,
  NotificationPrimitive,
  Toast,
  Tooltip,
} from '@ingrid/luterkort-react';
import { ToastCompositeProps } from '@ingrid/luterkort-react/dist/Notification/ToastComposite';

import { ConditionalWrapper } from '~src/components/ConditionalWrapper/ConditionalWrapper';
import { useTranslation } from '~src/i18n/use-translation';
import { usePagination } from '~src/main/hooks/usePagination';
import { usePermissions } from '~src/main/hooks/usePermissions';
import { useToast } from '~src/main/hooks/useToast';
import { TrackingEventEnum, useTracking } from '~src/main/hooks/useTracking';
import { stringifyDateRange } from '~src/utils/date';
import { stringifyDateTimeRange } from '~src/utils/date';
import { extractFileName, extractShipmentReportFileName } from '~src/utils/file';

import {
  useCreateShipmentReportMutation,
  useFetchShippingLabelsMutation,
  useGenerateLabelsUrlMutation,
  useGenerateReportUrlMutation,
  useShipmentBookParcelsBulkMutation,
  useShipmentSchedulePickupMutation,
} from '../mutations';
import { useShipmentListPolling, useShipmentReportPolling } from '../polling';
import { useShipmentsListQuery } from '../queries';
import { BulkExportDialog, FormValues as BulkExportFormValues } from './bulk-export-dialog';
import { useShipmentListFilter } from './filters/useShipmentListFilter';
import { GroupStatus } from './groups';

export function ActionPanel() {
  const [actionToast, setActionToast] = useState<ToastCompositeProps>();
  const [isBulkDialogOpen, setIsBulkDialogOpen] = useState(false);

  const { selectionManager } = InteractiveTable.useContext();

  const createShipmentReportMutation = useCreateShipmentReportMutation();
  const schedulePickupMutation = useShipmentSchedulePickupMutation();
  const bookBulkMutation = useShipmentBookParcelsBulkMutation();
  const fetchLabelsMutation = useFetchShippingLabelsMutation();
  const reportUrlMutation = useGenerateReportUrlMutation();
  const shipmentReportPolling = useShipmentReportPolling();
  const labelUrlMutation = useGenerateLabelsUrlMutation();
  const shipmentListPolling = useShipmentListPolling();
  const selected = selectionManager.getSelectedKeys();
  const tracking = useTracking();

  const { data: shipments = [], isLoading: shipmentsIsLoading } = useShipmentsListQuery(
    {
      filtering: { shipment_ids: selected },
      pageSize: selected.length,
    },
    selected.length > 0
  );

  const { filtering, advancedFiltering, setFiltering, clearFilters } = useShipmentListFilter();
  const { setPageIndex } = usePagination();
  const { hasRole } = usePermissions();
  const { t } = useTranslation();
  const { toast } = useToast();

  const selectedShipment = shipments.filter(s => (s.id ? selected.includes(s.id) : false));
  const preventEdit = !hasRole(['transport:edit']);
  const hasSelected = shipments.length > 0;

  const isSelectedPrintable = hasSelected && shipments.every(s => s.status !== 'CREATED' && s.status !== 'CANCELED');
  const isSelectedPickupable = hasSelected && shipments.every(s => s.status === 'BOOKED');
  const isSelectedBookable = hasSelected && selectedShipment.every(s => s.status === 'CREATED');

  async function handlePreviewClick(excludedIds: string[], setter: (value: string) => void) {
    const base64EncodedIds = btoa(excludedIds.join(','));
    clearFilters();
    await setPageIndex(1);
    setter(base64EncodedIds);
    selectionManager.clearAll();
  }

  async function handleExportClicked() {
    const isAllSelected = selectionManager.isSelectedVirtualAll();

    if (isAllSelected && (!advancedFiltering.createdAtRange?.start || !advancedFiltering.createdAtRange?.end)) {
      setIsBulkDialogOpen(true);
    } else {
      await exportCsv(
        isAllSelected && advancedFiltering.createdAtRange
          ? { createdAtRange: advancedFiltering.createdAtRange }
          : undefined
      );
    }
  }

  async function exportCsv(payload?: BulkExportFormValues) {
    const promise = async () => {
      const {
        data: { id },
      } = await createShipmentReportMutation.mutateAsync({
        ids: !selectionManager.isSelectedVirtualAll() ? selected : undefined,
        filtering: {
          statuses: filtering.group !== 'ALL' ? GroupStatus[filtering.group] : filtering.statuses,
          shipping_date_range: stringifyDateRange(advancedFiltering.shippingDateRange),
          created_at_range: stringifyDateTimeRange(payload?.createdAtRange),
          address_from_countries: advancedFiltering.originCountries,
          address_to_countries: advancedFiltering.deliveryCountries,
          shipping_methods: filtering.carriers,
        },
      });

      const poll = await shipmentReportPolling.mutateAsync({
        pollUntil: r => r?.shipments?.status === 'DONE',
        variables: id!,
      });

      const { data: report } = await reportUrlMutation.mutateAsync({
        file_name: extractShipmentReportFileName(poll!.shipments!.uri!),
      });

      window.open(report.url);
    };

    await toast.promise(promise, {
      success: { title: t('shipments.list.export.success') },
      error: { title: t('shipments.list.export.error') },
      loading: { title: t('shipments.list.export.loading') },
    });
  }

  async function bookSelected() {
    setActionToast({
      title: t('shipments.list.bookLoading'),
      variant: 'loading',
    });

    try {
      const {
        data: { succeeded_shipment_ids: includedIds = [], failed_shipment_ids: excludedIds = [] },
      } = await bookBulkMutation.mutateAsync({ shipment_ids: selected });

      if (includedIds.length > 0) {
        shipmentListPolling.mutate({
          variables: includedIds,
          pollUntil: (shipments = []) => shipments.filter(s => s.status === 'BOOKED').length === includedIds.length,
        });
      }

      if (excludedIds.length > 0) {
        setActionToast({
          action: (
            <NotificationPrimitive.Link
              onClick={() => handlePreviewClick(excludedIds, v => setFiltering(p => ({ ...p, excludedBookIds: v })))}
            >
              {t('common.preview')}
            </NotificationPrimitive.Link>
          ),
          title: t('shipments.list.bookPartialError.title', {
            failedCount: excludedIds.length,
            totalCount: selected.length,
          }),
          subtitle: t('shipments.list.bookPartialError.subtitle'),
          variant: 'warning',
        });
      } else {
        setActionToast({
          title: t('shipments.list.bookSuccess'),
          variant: 'success',
        });
      }

      for (const id of includedIds) {
        tracking.capture(TrackingEventEnum.ShipmentBookingCompleted, { shipmentId: id });
      }

      for (const id of excludedIds) {
        tracking.capture(TrackingEventEnum.ShipmentBookingFailed, { shipmentId: id });
      }
    } catch (error) {
      setActionToast({
        title: t('shipments.list.bookError'),
        variant: 'critical',
      });

      throw error;
    }
  }

  async function printSelected() {
    setActionToast({
      title: t('shipment.details.printLoading'),
      variant: 'loading',
    });

    try {
      const {
        data: { not_included_shipments_ids: excludedIds = [], labels_urls },
      } = await fetchLabelsMutation.mutateAsync({
        shipment_ids: selected,
      });

      const { data: result } = await labelUrlMutation.mutateAsync({
        // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
        file_name: extractFileName(labels_urls?.[0] as string),
      });

      if (excludedIds?.length) {
        setActionToast({
          action: (
            <NotificationPrimitive.Link
              onClick={() =>
                handlePreviewClick(excludedIds, v => setFiltering(p => ({ ...p, setExcludedPrintIds: v })))
              }
            >
              {t('common.preview')}
            </NotificationPrimitive.Link>
          ),
          title: t('shipments.list.printPartialError.title', {
            failedCount: excludedIds.length,
            totalCount: selected.length,
          }),
          subtitle: t('shipments.list.printPartialError.subtitle'),
          variant: 'warning',
        });
      } else {
        setActionToast({
          title: t('shipment.details.printSuccess'),
          variant: 'success',
        });
      }

      window.open(result?.url);
    } catch (error) {
      setActionToast({
        title: t('shipment.details.printError'),
        variant: 'critical',
      });

      throw error;
    }
  }

  async function schedulePickupSelected() {
    setActionToast({
      title: t('shipments.list.schedulePickupLoading'),
      variant: 'loading',
    });

    const successfulIds: string[] = [];
    const failedIds: string[] = [];

    for (const id of selected) {
      try {
        await schedulePickupMutation.mutateAsync({ id });
        tracking.capture(TrackingEventEnum.ShipmentSchedulePickupCompleted, { shipmentId: id });
        successfulIds.push(id);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        tracking.capture(TrackingEventEnum.ShipmentSchedulePickupFailed, { shipmentId: id });
        failedIds.push(id);
      }
    }

    /**
     * Given that there's any successful mutation, we should poll for changes
     */
    if (successfulIds.length > 0) {
      await shipmentListPolling.mutateAsync({
        variables: successfulIds,
        pollUntil: (shipments = []) =>
          shipments.filter(s => s.status === 'PICKUP_SCHEDULED').length === successfulIds.length,
      });
    }

    if (failedIds.length > 0) {
      setActionToast({
        action: (
          <NotificationPrimitive.Link
            onClick={() =>
              handlePreviewClick(failedIds, v => setFiltering(p => ({ ...p, setExcludedSchedulePickupIds: v })))
            }
          >
            {t('common.preview')}
          </NotificationPrimitive.Link>
        ),
        title: t('shipments.list.schedulePickupPartialError.title', {
          failedCount: failedIds.length,
          totalCount: selected.length,
        }),
        subtitle: t('shipments.list.schedulePickupPartialError.subtitle'),
        variant: 'warning',
      });
    } else {
      setActionToast({
        title: t('shipments.list.schedulePickupSuccess'),
        variant: 'success',
      });
    }
  }

  return (
    <>
      <ActionPanelUI.Root style={{ width: '100%', maxWidth: '1008px' }}>
        <ActionPanelUI.Selection />

        <ActionPanelUI.Inline>
          <ActionPanelUI.SelectAllButton />
          <ActionPanelUI.DeselectAllButton />
        </ActionPanelUI.Inline>

        <ActionPanelUI.Actions>
          <ConditionalWrapper
            wrapper={c => (
              <Tooltip content={preventEdit ? t('auth.forbidden') : t('shipments.list.selectedNotBookable')}>
                {c}
              </Tooltip>
            )}
            when={preventEdit || !isSelectedBookable}
          >
            <ActionPanelUI.LoadingButton
              loading={bookBulkMutation.isPending || shipmentsIsLoading}
              disabled={preventEdit || !isSelectedBookable}
              onClick={bookSelected}
            >
              {t('common.book')}
            </ActionPanelUI.LoadingButton>
          </ConditionalWrapper>

          <ConditionalWrapper
            wrapper={c => <Tooltip content={t('shipments.list.selectedNotPrintable')}>{c}</Tooltip>}
            when={!isSelectedPrintable}
          >
            <ActionPanelUI.LoadingButton
              loading={fetchLabelsMutation.isPending || labelUrlMutation.isPending || shipmentsIsLoading}
              disabled={!isSelectedPrintable}
              onClick={printSelected}
            >
              {t('common.printLabels')}
            </ActionPanelUI.LoadingButton>
          </ConditionalWrapper>

          <ConditionalWrapper
            wrapper={c => (
              <Tooltip content={preventEdit ? t('auth.forbidden') : t('shipments.list.selectedNotSchedulePickupable')}>
                {c}
              </Tooltip>
            )}
            when={preventEdit || !isSelectedPickupable}
          >
            <ActionPanelUI.LoadingButton
              loading={bookBulkMutation.isPending || shipmentsIsLoading}
              disabled={preventEdit || !isSelectedPickupable}
              onClick={schedulePickupSelected}
            >
              {t('common.schedulePickup')}
            </ActionPanelUI.LoadingButton>
          </ConditionalWrapper>

          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <ActionPanelUI.IconButton />
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="end" side="top">
                <DropdownMenu.Item onClick={handleExportClicked}>{t('common.exportCsv')}</DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </ActionPanelUI.Actions>
      </ActionPanelUI.Root>

      <BulkExportDialog
        onSubmit={async data => {
          await exportCsv(data);
          setIsBulkDialogOpen(false);
        }}
        setIsOpen={setIsBulkDialogOpen}
        isOpen={isBulkDialogOpen}
      />

      {actionToast && <Toast defaultOpen {...actionToast} onOpenChange={() => setActionToast(undefined)} />}
    </>
  );
}
